import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * A custom HTML element that listens for variant updates
 * and dynamically updates its content based on the specified query selector.
 *
 * This element registers an event listener for `ThemeEvents.variantUpdate`
 * when connected to the DOM, and removes it when disconnected.
 *
 * @extends HTMLElement
 */
class VariantContentUpdate extends HTMLElement {
  /**
   * The CSS selector string used to locate the content node
   * within the variant update HTML source.
   *
   * @type {string|null}
   * @private
   */
  #queryIdentifier = null;

  /**
   * Called automatically when the element is inserted into the DOM.
   * Adds an event listener for variant updates to handle dynamic content replacement.
   *
   * @returns {void}
   */
  connectedCallback() {
    this.#queryIdentifier = this.dataset.variantUpdateIdentifier;

    if (this.#queryIdentifier) {
      document.addEventListener(
        ThemeEvents.variantUpdate,
        this.updateContent,
      );
    }
  }

  /**
   * Called automatically when the element is removed from the DOM.
   * Cleans up the event listener for variant updates to prevent memory leaks.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    if (this.#queryIdentifier) {
      document.removeEventListener(
        ThemeEvents.variantUpdate,
        this.updateContent,
      );
    }
  }

  /**
   * Handles the `variantUpdate` event by replacing the current element
   * with the corresponding new content from the updated variant HTML.
   *
   * @param {VariantUpdateEvent} event - The variant update event containing new HTML.
   * @returns {void}
   */
  updateContent = (event) => {
    if (!this.#queryIdentifier) return;

    const source = event.detail.data.html;

    if (!source) return;

    const newContent = source.querySelector(`[data-variant-update-identifier="${this.#queryIdentifier}"]`);

    if (!newContent) return;

    this.replaceWith(newContent);
  };
}

if (!customElements.get("variant-content-update")) {
  customElements.define("variant-content-update", VariantContentUpdate);
}
